import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  Project,
  PullTestingModule,
  PullTestingModuleLifecycleStatus,
  PiledModule,
  stripProjectModuleEmbeds,
} from 'shared_models';
import { ProjectService } from './project.service';
import { PullTestingService } from './pull-testing.service';
import { AppContextService } from './app-context.service';
import { LoggerService } from './logger.service';

export type ProjectFacadePullTestingStatus = PullTestingModuleLifecycleStatus | 'missing' | null;

export interface ProjectModulesFacadeState {
  projectID: string | null;
  phase: 'idle' | 'loading' | 'ready' | 'error';
  project: Project | null;
  piledModule: PiledModule | null;
  pullTestingModule: PullTestingModule | null;
  pullTestingStatus: ProjectFacadePullTestingStatus;
  errorMessage: string | null;
}

const initialState: ProjectModulesFacadeState = {
  projectID: null,
  phase: 'idle',
  project: null,
  piledModule: null,
  pullTestingModule: null,
  pullTestingStatus: null,
  errorMessage: null,
};

/**
 * Single entry for loading the current project (`GET /projects/{id}`), Piled module
 * (`GET /projects/{id}/piled`), and Pull Testing module config. Publishes siblings
 * `{ project, piledModule, pullTestingModule }` (project strip has no embedded modules)
 * and mirrors into {@link AppContextService}.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectFacadeService {
  private readonly state$ = new BehaviorSubject<ProjectModulesFacadeState>({ ...initialState });
  private readonly inflight = new Map<string, Promise<void>>();

  constructor(
    private projectService: ProjectService,
    private pullTestingService: PullTestingService,
    private appContext: AppContextService,
    private logger: LoggerService
  ) {}

  /** Observable full facade state (use `async` pipe or explicit `subscribe` with teardown). */
  get state(): Observable<ProjectModulesFacadeState> {
    return this.state$.asObservable();
  }

  /** Latest synchronous snapshot (for imperative init after `await loadProjectModules`). */
  getSnapshot(): ProjectModulesFacadeState {
    return this.state$.getValue();
  }

  /** Emits whenever `project` changes (reference or id). */
  get project$(): Observable<Project | null> {
    return this.state$.pipe(
      map((s) => s.project),
      distinctUntilChanged((a, b) => a?.projectID === b?.projectID && a === b)
    );
  }

  /** Aligns facade with the route’s project id (alias for {@link loadProjectModules}). */
  setFromRoute(projectID: string | null): Promise<void> {
    if (!projectID) {
      this.reset();
      return Promise.resolve();
    }
    return this.loadProjectModules(projectID);
  }

  /**
   * Awaits {@link loadProjectModules} and returns the ready facade snapshot, or null on failure.
   */
  async ensureLoaded(projectID: string): Promise<ProjectModulesFacadeState | null> {
    await this.loadProjectModules(projectID);
    const s = this.getSnapshot();
    return s.phase === 'ready' ? s : null;
  }

  /**
   * Loads project, Piled module, and pull-testing module config, updates {@link AppContextService}, and publishes state.
   * Concurrent calls for the same `projectID` share one in-flight promise.
   *
   * Pass `{ force: true }` to skip the facade "already ready" short-circuit and to bypass {@link ProjectService}
   * in-memory cache for `GET /projects/{id}` (always refetches piled + pull-testing endpoints).
   */
  loadProjectModules(projectID: string, options?: { force?: boolean }): Promise<void> {
    if (options?.force) {
      return this.runLoadProjectModules(projectID, true);
    }
    const existing = this.inflight.get(projectID);
    if (existing) {
      return existing;
    }
    const run = this.runLoadProjectModules(projectID, false).finally(() => {
      if (this.inflight.get(projectID) === run) {
        this.inflight.delete(projectID);
      }
    });
    this.inflight.set(projectID, run);
    return run;
  }

  /** Clears published state (e.g. leaving project scope). */
  reset(): void {
    this.state$.next({ ...initialState });
  }

  private async runLoadProjectModules(projectID: string, forceReload: boolean): Promise<void> {
    const prev = this.getSnapshot();
    if (!forceReload) {
      /**
       * Skip refetch only when pull-testing state is not expected to change without a reload.
       * - **null/null**: GET failed non-definitively (e.g. 5xx) — must retry.
       * **`missing` (404)**: true at last fetch but can flip right after the setup wizard creates the module;
       *   must not short-circuit or install/test pages see stale "not configured".
       */
      const mustRefetchPullTesting =
        (prev.pullTestingModule == null && prev.pullTestingStatus == null) ||
        prev.pullTestingStatus === 'missing';
      if (
        prev.phase === 'ready' &&
        prev.projectID === projectID &&
        prev.project &&
        Array.isArray(prev.piledModule?.pilingAttributes) &&
        !mustRefetchPullTesting
      ) {
        return;
      }
    }

    const preserveForSameProject = prev.projectID === projectID;
    this.state$.next({
      projectID,
      phase: 'loading',
      project: preserveForSameProject ? prev.project : null,
      piledModule: preserveForSameProject ? prev.piledModule : null,
      pullTestingModule: preserveForSameProject ? prev.pullTestingModule : null,
      pullTestingStatus: preserveForSameProject ? prev.pullTestingStatus : null,
      errorMessage: null,
    });

    try {
      const [projResp, piledResult, pullTestingResult] = await Promise.all([
        firstValueFrom(this.projectService.getProject(projectID, forceReload)),
        this.fetchPiledModule(projectID),
        this.fetchPullTestingModule(projectID),
      ]);

      const rawProject = projResp.data ?? null;
      if (!rawProject) {
        this.state$.next({
          projectID,
          phase: 'error',
          project: preserveForSameProject ? prev.project : null,
          piledModule: preserveForSameProject ? prev.piledModule : null,
          pullTestingModule: preserveForSameProject ? prev.pullTestingModule : null,
          pullTestingStatus: preserveForSameProject ? prev.pullTestingStatus : null,
          errorMessage: 'Failed to load project.',
        });
        return;
      }

      const project = stripProjectModuleEmbeds(rawProject);
      const { piledModule } = piledResult;
      const { pullTestingModule, pullTestingStatus } = pullTestingResult;

      this.appContext.setProjectModulesContext({
        project,
        piledModule,
        pullTestingModule,
      });

      this.state$.next({
        projectID,
        phase: 'ready',
        project,
        piledModule,
        pullTestingModule,
        pullTestingStatus,
        errorMessage: null,
      });
    } catch (e: unknown) {
      this.logger.error('ProjectFacade: loadProjectModules failed', e);
      this.state$.next({
        projectID,
        phase: 'error',
        project: preserveForSameProject ? prev.project : null,
        piledModule: preserveForSameProject ? prev.piledModule : null,
        pullTestingModule: preserveForSameProject ? prev.pullTestingModule : null,
        pullTestingStatus: preserveForSameProject ? prev.pullTestingStatus : null,
        errorMessage: 'Failed to load project.',
      });
    }
  }

  private async fetchPiledModule(
    projectID: string
  ): Promise<{ piledModule: PiledModule | null }> {
    try {
      const piledResp = await firstValueFrom(this.projectService.getPiledModule(projectID));
      return { piledModule: piledResp.data ?? null };
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      if (status === 404) {
        return { piledModule: null };
      }
      this.logger.warn('ProjectFacade: piled module load failed', e);
      return { piledModule: null };
    }
  }

  private async fetchPullTestingModule(
    projectID: string
  ): Promise<{ pullTestingModule: PullTestingModule | null; pullTestingStatus: ProjectFacadePullTestingStatus }> {
    try {
      const modRes = await firstValueFrom(this.pullTestingService.getModuleConfig(projectID));
      const pullTestingModule = modRes?.data ?? null;
      const st = pullTestingModule?.status as PullTestingModuleLifecycleStatus | undefined;
      const pullTestingStatus: ProjectFacadePullTestingStatus =
        st === 'pending' || st === 'active' || st === 'completed'
          ? st
          : pullTestingModule
            ? 'active'
            : 'missing';
      return { pullTestingModule, pullTestingStatus };
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      if (status !== 404) {
        this.logger.warn('ProjectFacade: pull-testing module load failed', e);
      }
      return {
        pullTestingModule: null,
        pullTestingStatus: status === 404 ? 'missing' : null,
      };
    }
  }
}
